/* Expiries. Eight registers, one list, ordered by how soon it hurts.

   Nothing here is stored — it is recomputed from the collections every render,
   which means it works identically offline against the IndexedDB cache. */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDepotScope, inScope } from '../../hooks/useDepotScope';
import { buildExpiries, dueWithin } from '../../lib/expiries';
import { countdown, fmtDate } from '../../lib/format';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Controls,
  EmptyState,
  Meter,
  Panel,
  SearchBox,
  Segmented,
  Select,
  TableSkeleton,
  LoadError,
} from '../ui';
import { Tile, Tiles } from './Tile';
import s from './screens.module.scss';

const WINDOWS = [
  /* The dashboard's "Expiring within 90 days" opens here, and a list of the next six
     months under a card that says 90 days is a list that does not match its number. */
  { value: '90', label: 'Next 90 days' },
  { value: '180', label: 'Next 6 months' },
  { value: '365', label: 'Next year' },
  { value: '99999', label: 'Everything' },
];

export function ExpiriesScreen() {
  const svc = useServices();
  const { scope } = useDepotScope();

  const [params] = useSearchParams();
  const [within, setWithin] = useState(() => {
    const w = params.get('within');
    return WINDOWS.some((x) => x.value === w) ? (w as string) : '180';
  });
  const [source, setSource] = useState('all');
  const [query, setQuery] = useState('');

  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });
  const people = useQuery({ queryKey: qk.people, queryFn: () => svc.people.list() });
  const suppliers = useQuery({ queryKey: qk.suppliers, queryFn: () => svc.suppliers.list() });
  const domains = useQuery({ queryKey: qk.domains, queryFn: () => svc.domains.list() });
  const applications = useQuery({ queryKey: qk.applications, queryFn: () => svc.applications.list() });
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });
  const articles = useQuery({ queryKey: qk.articles, queryFn: () => svc.articles.list() });
  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const recordTypes = useQuery({ queryKey: qk.recordTypes, queryFn: () => svc.recordTypes.types() });
  const customRecords = useQuery({ queryKey: qk.customRecords(), queryFn: () => svc.recordTypes.records() });

  const loading = devices.isLoading || suppliers.isLoading;

  /* Every source, not just the two that drive the skeleton. This screen is an
     aggregation across nine registers, so one silent failure does not show up as
     an error — it shows up as a renewal that is not in the list. Partial is worse
     here than anywhere else in the app, so any failure suppresses the whole
     table rather than quietly under-reporting. */
  const failed = [
    devices,
    people,
    suppliers,
    domains,
    applications,
    credentials,
    articles,
    skus,
    recordTypes,
    customRecords,
  ].find((q) => q.isError);

  const all = useMemo(
    () =>
      buildExpiries({
        devices: devices.data,
        people: people.data,
        suppliers: suppliers.data,
        domains: domains.data,
        applications: applications.data,
        credentials: credentials.data,
        articles: articles.data,
        skus: skus.data,
        recordTypes: recordTypes.data,
        customRecords: customRecords.data,
      }).filter((e) => inScope(scope, e.loc ?? scope)),
    [
      devices.data,
      people.data,
      suppliers.data,
      domains.data,
      applications.data,
      credentials.data,
      articles.data,
      skus.data,
      recordTypes.data,
      customRecords.data,
      scope,
    ],
  );

  const sources = useMemo(() => [...new Set(all.map((e) => e.source))].sort(), [all]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return dueWithin(all, Number(within)).filter((e) => {
      if (source !== 'all' && e.source !== source) return false;
      if (!q) return true;
      return e.label.toLowerCase().includes(q) || e.detail.toLowerCase().includes(q);
    });
  }, [all, within, source, query]);

  const overdue = all.filter((e) => e.days < 0);
  const in30 = all.filter((e) => e.days >= 0 && e.days <= 30);
  const in90 = all.filter((e) => e.days >= 0 && e.days <= 90);

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Expiries</h1>
          <p>
            Warranties, contracts, licence terms, domains, certificates and reviews — every
            date in the system, in one place.
          </p>
        </div>
      </div>

      <div className={s.stack}>
        <Tiles>
          <Tile icon="ti-alarm" value={overdue.length} label="Already passed" tone="bad" />
          <Tile icon="ti-clock-hour-3" value={in30.length} label="Within 30 days" tone="warn" />
          <Tile icon="ti-calendar" value={in90.length} label="30 to 90 days" tone="warn" />
          <Tile
            icon="ti-bell-exclamation"
            value={all.filter((e) => e.id.startsWith('notice-') && e.days >= 0 && e.days <= 90).length}
            label="Notice deadlines"
            foot="Miss one and it has rolled over"
            tone="ok"
          />
        </Tiles>

        {/* The stack, not a bare div. Without it the filter bar and the panel
            below sit flush against each other, because the page's rhythm is a
            gap between its own children and this pair are one child. */}
        <div className={s.stack}>
          <Controls>
            <SearchBox value={query} onChange={setQuery} placeholder="Filter by name or supplier…" />
            <Select
              label="Register"
              value={source}
              onChange={setSource}
              options={[
                { value: 'all', label: 'All registers' },
                ...sources.map((x) => ({ value: x, label: x })),
              ]}
            />
            <Segmented label="Window" value={within} onChange={setWithin} options={WINDOWS} />
          </Controls>

          <Panel flush>
            {loading ? (
              <TableSkeleton />
            ) : failed ? (
              <LoadError error={failed.error} what="the expiry aggregation" />
            ) : rows.length === 0 ? (
              <EmptyState icon="ti-calendar-check" title="Nothing in this window">
                Widen the range or clear the filters.
              </EmptyState>
            ) : (
              rows.map((e) => {
                const tone = e.days < 0 ? 'bad' : e.days < 60 ? 'warn' : 'ok';
                return (
                  <Link key={e.id} to={e.href} className={s.exprow}>
                    <span
                      className={`${s.expIcon} ${
                        tone === 'bad' ? s.expRed : tone === 'warn' ? s.expAmber : s.expGreen
                      }`}
                    >
                      <i className={`ti ${e.icon}`} aria-hidden />
                    </span>
                    <div className={s.expText}>
                      <b>{e.label}</b>
                      <small>
                        {e.source} · {e.detail}
                      </small>
                    </div>
                    {/* A year of runway is a full bar; the closer it gets the more
                        of it is gone. Purely a glance aid — the date is the fact. */}
                    <div className={s.expBar}>
                      <Meter value={Math.max(2, Math.min(100, 100 - e.days / 3.65))} max={100} tone={tone} />
                    </div>
                    <div className={s.expWhen}>
                      <b>{fmtDate(e.due)}</b>
                      <small>{e.days < 0 ? 'overdue' : countdown(e.due)}</small>
                    </div>
                  </Link>
                );
              })
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
